<template>
  <WdsAlert v-if="validationErrors.length" error class="tw-mb-2">
    <ul class="tw-mb-0">
      <li v-for="validationError in validationErrors" :key="validationError">
        {{ validationError }}
      </li>
    </ul>
  </WdsAlert>
  <WdsStack v-if="clientPending" direction-row justify-center class="tw-py-16">
    <WdsSpinner indeterminate data-test="share-client-loading" />
  </WdsStack>
  <template v-else>
    <WdsStack direction-row align-center justify-space-between gap-md class="tw-mb-2">
      <WdsText v-if="client" secondary data-test="share-client-notice">
        {{
          $t(
            'This vacancy is for :client, so the list starts on the staffing agencies linked to them.',
            {
              client: client.name,
            },
          )
        }}
        <template v-if="sharedOutsideClientCount > 0 && !preSelectionCleared">
          {{
            $tc(
              'The selected count includes :count agency this vacancy is already shared with that is not linked to :client. It stays shared. | The selected count includes :count agencies this vacancy is already shared with that are not linked to :client. They stay shared.',
              sharedOutsideClientCount,
              { client: client.name },
            )
          }}
        </template>
      </WdsText>
      <div class="tw-flex tw-shrink-0 tw-ml-auto tw-whitespace-nowrap">
        <WdsButtonGhost
          v-if="totalCount > 0"
          secondary
          :disabled="selectAll"
          data-test="share-select-all"
          @click="selectAll = true"
        >
          {{
            selectAll
              ? $t('Selecting all agencies…')
              : $tc('Select :count agency | Select all :count agencies', totalCount)
          }}
        </WdsButtonGhost>
      </div>
    </WdsStack>
    <WdsAlert
      v-if="preSelectionCleared && sharesToRemoveCount > 0"
      warning
      icon
      class="tw-mb-2"
      data-test="share-unshare-warning"
    >
      {{
        $tc(
          'Updating will unshare :count staffing agency, including agencies not shown in this list. | Updating will unshare :count staffing agencies, including agencies not shown in this list.',
          sharesToRemoveCount,
        )
      }}
    </WdsAlert>
    <CompanyRecruitersBaseTable
      v-model:selected-rows="selectedRows"
      v-model:total-count="totalCount"
      v-model:select-all="selectAll"
      :filters="initialFilters"
      :bulk-actions="bulkActions"
      contain-bulk-action-bar
      :is-row-pre-selected="isSharedWithJob"
      :pre-selected-count="preSelectionCleared ? 0 : preSelectedRecruiters.length"
      @update:pre-selected-count="preSelectionCleared = true"
      max-height-adjustment="20vh"
      :default-per-page="50"
      :default-per-page-options="[10, 50, 250, 500]"
    />
  </template>
  <div
    v-if="loading"
    class="-tw-mx-6 select-talent-footer tw-flex tw-items-center tw-justify-end tw-pt-4 tw-pb-5 tw-px-6"
  >
    <WdsSpinner indeterminate />
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, Ref, ref, watch } from 'vue'
import { WdsAlert, WdsButtonGhost, WdsSpinner, WdsStack, WdsText } from '@worksome/wds-components'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { $t, $tc } from '@/composables/translation'
import { useToast } from '@/composables/useToast'
import useApolloError from '@/composables/useApolloError'
import CompanyRecruitersBaseTable from '@/components/Contacts/CompanyRecruiter/CompanyRecruitersBaseTable.vue'
import { Recruiter } from '@/components/Job/hiring-types'
import {
  CreateJobShareMutation,
  RemoveJobShareMutation,
} from '@/components/Job/Share/graphql/mutations'
import { JobFragment } from '@/components/Job/graphql/fragments'
import { deepClone } from '@/utils/tools'
import { FragmentType, graphql, useFragment } from '@/gql'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'
import { useAccountsStore } from '@/components/GraphqlDataTable/filters/accounts/accounts-store'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import { DataTableBulkAction } from '@/components/GraphqlDataTable/types'
import { NetworkError } from '@/utils/notification'

interface CompanyRecruiterRow {
  id: string
  recruiter: {
    id: string
    name: string
    avatar: string
  }
}

const props = withDefaults(
  defineProps<{
    jobId: string
    preSelectedRecruiters: Recruiter[]
  }>(),
  {
    preSelectedRecruiters: () => [],
  },
)

const emit = defineEmits(['updated', 'cancel'])

const toast = useToast()

const { actsAsSupplier } = useAccountCapabilities()
const { selectedAccounts } = useAccountsStore()

const JOB_SHARE_CLIENT_QUERY = graphql(`
  query JobShareClient($jobId: ID!) {
    job(id: $jobId) {
      id
      sourceJob {
        id
        company {
          id
          name
        }
      }
    }
  }
`)

const CLIENT_AGENCIES_QUERY = graphql(`
  query JobShareClientAgencies($accounts: [ID!], $endClients: [ID!], $first: Int!) {
    companyRecruiters(accounts: $accounts, endClients: $endClients, first: $first) {
      data {
        id
        recruiter {
          id
        }
      }
      paginatorInfo {
        hasMorePages
      }
    }
  }
`)

const { result: clientResult, loading: clientLoading } = useQuery(
  JOB_SHARE_CLIENT_QUERY,
  () => ({ jobId: props.jobId }),
  () => ({
    clientId: 'publicApi',
    enabled: actsAsSupplier.value,
    context: { skipGlobalErrorHandling: true },
  }),
)

// The table reads its filters once on mount, so it waits for the client.
const clientPending = computed(
  () => actsAsSupplier.value && clientLoading.value && clientResult.value === undefined,
)

const client = computed(() =>
  actsAsSupplier.value ? (clientResult.value?.job?.sourceJob?.company ?? null) : null,
)

const initialFilters = computed(() =>
  client.value ? { endClients: [{ label: client.value.name, value: client.value.id }] } : {},
)

const { result: clientAgenciesResult } = useQuery(
  CLIENT_AGENCIES_QUERY,
  () => ({
    endClients: client.value ? [client.value.id] : [],
    first: PER_PAGE_MAXIMUM,
    ...(selectedAccounts.length === 0
      ? {}
      : { accounts: selectedAccounts.map((account) => account.id) }),
  }),
  () => ({
    clientId: 'publicApi',
    enabled: !!client.value && props.preSelectedRecruiters.length > 0,
    context: { skipGlobalErrorHandling: true },
  }),
)

const sharedOutsideClientCount = computed(() => {
  const agencies = clientAgenciesResult.value?.companyRecruiters
  if (!agencies || agencies.paginatorInfo.hasMorePages) return 0

  const linked = new Set(agencies.data.map((row) => row.recruiter?.id))
  return props.preSelectedRecruiters.filter((r) => !linked.has(r.recruiterId)).length
})

const totalCount = ref(0)
const selectAll = ref(false)
const selectedRows = ref<Record<string, object>>({})
// Set once the user clears the selection: shares not shown in the table may then be removed too.
const preSelectionCleared = ref(false)

const sharesToRemoveCount = computed(() => {
  const selected = new Set(
    (Object.values(selectedRows.value) as CompanyRecruiterRow[]).map((row) => row.recruiter?.id),
  )
  return props.preSelectedRecruiters.filter((r) => !selected.has(r.recruiterId)).length
})

const sharedRecruiterIds = computed(
  () => new Set(props.preSelectedRecruiters.map((r) => r.recruiterId)),
)

const loadedSharedRecruiterIds = new Set<string>()

// Reset if this dialog is ever pointed at a different job without remounting,
// so loaded ids from a previous job can't leak into the next save.
watch(
  () => props.jobId,
  () => {
    loadedSharedRecruiterIds.clear()
    preSelectionCleared.value = false
  },
)

const isSharedWithJob = (row: object): boolean => {
  const recruiterId = (row as CompanyRecruiterRow).recruiter?.id
  if (recruiterId && sharedRecruiterIds.value.has(recruiterId)) {
    loadedSharedRecruiterIds.add(recruiterId)
    return true
  }
  return false
}

const updateJobInCache = (cache: any, isForRecruiters: boolean, recruiterSharesCount: number) => {
  const job = deepClone(
    cache.readFragment({
      id: `Job:${props.jobId}`,
      fragment: JobFragment,
    }),
  )

  if (job) {
    job.isForRecruiters = isForRecruiters
    job.recruiterShares.paginatorInfo.total = recruiterSharesCount
    cache.writeFragment({
      id: `Job:${props.jobId}`,
      fragment: JobFragment,
      data: { ...job },
    })
  }
}

const {
  loading: createJobShareLoading,
  mutate: mutateCreateJobShare,
  error: errorCreateJobShare,
} = useMutation(CreateJobShareMutation, {
  clientId: 'publicApi',
  update(cache, jobShare) {
    const job = jobShare.data?.createJobShare[0].job as FragmentType<typeof JobFragment>
    const jobObj = useFragment(JobFragment, job)
    updateJobInCache(cache, jobObj?.isForRecruiters, jobObj?.recruiterShares.paginatorInfo.total)
  },
})

const {
  loading: removeJobShareLoading,
  mutate: mutateRemoveJobShare,
  error: errorRemoveJobShare,
} = useMutation(RemoveJobShareMutation, {
  clientId: 'publicApi',
  update(cache, jobShare) {
    const job = jobShare.data?.removeJobShare[0].job as FragmentType<typeof JobFragment>
    const jobObj = useFragment(JobFragment, job)
    updateJobInCache(cache, jobObj?.isForRecruiters, jobObj?.recruiterShares.paginatorInfo.total)
  },
})

const loading = computed(() => createJobShareLoading.value || removeJobShareLoading.value)

const { validationErrors: validationErrorsCreate }: { validationErrors: Ref<string[]> } =
  useApolloError(errorCreateJobShare)
const { validationErrors: validationErrorsRemove }: { validationErrors: Ref<string[]> } =
  useApolloError(errorRemoveJobShare)

const validationErrors = computed(() => [
  ...Object.values(validationErrorsCreate.value),
  ...Object.values(validationErrorsRemove.value),
])

const handleUpdateShares = async (selection: Record<string, object>) => {
  try {
    const rows = Object.values(selection) as CompanyRecruiterRow[]
    const selectedRecruiterIds = new Set(rows.map((row) => row.recruiter?.id).filter(Boolean))

    const recruiterIdsToShare = [...selectedRecruiterIds].filter(
      (id) => !sharedRecruiterIds.value.has(id),
    )

    const shareIdsToRemove = props.preSelectedRecruiters
      .filter(
        (r) =>
          (preSelectionCleared.value || loadedSharedRecruiterIds.has(r.recruiterId)) &&
          !selectedRecruiterIds.has(r.recruiterId),
      )
      .map((r) => r.id)

    if (recruiterIdsToShare.length > 0) {
      await mutateCreateJobShare({
        input: {
          job: props.jobId,
          accounts: recruiterIdsToShare,
        },
      })
    }

    if (shareIdsToRemove.length > 0) {
      await mutateRemoveJobShare({
        input: {
          ids: shareIdsToRemove,
        },
      })
    }

    if (recruiterIdsToShare.length > 0 || shareIdsToRemove.length > 0) {
      emit('updated')
      toast.success($t('Staffing agency shares updated'))
    }

    emit('cancel')
  } catch (ex: any) {
    new NetworkError({
      ex,
      msg: $t('An error occurred while updating staffing agency shares'),
    }).dispatch()
  }
}

const bulkActions: ComputedRef<DataTableBulkAction[]> = computed(() => [
  {
    name: $t('Update'),
    icon: 'checkmark-outline' as any,
    type: 'primary',
    buttonVariant: 'solid',
    disabled: loading.value || selectAll.value,
    dataTest: 'update-shares-bulk',
    action: handleUpdateShares,
    persistent: true,
  },
])
</script>

<style lang="scss" scoped>
@import 'resources/sass/frontend/_color-variables';

.select-talent-footer {
  border-top: 1px solid $gray-300;
}
</style>
